import { makeStyles } from '@material-ui/core';
import { CreateCSSProperties, CSSProperties } from '@material-ui/core/styles/withStyles';
import { baseColors } from '@ncr-retail/mui-theme';
import { rem, vh } from 'src/common/helpers/dimension-helper';
import { themeColor } from '../../mui-theme';

export interface ItemListStylesProps {
  pageSize?: number;
  ejListSize?: number;
  disabled?: boolean;
}

const baseColor = baseColors();

const lineBase: CreateCSSProperties = {
  height: ({ pageSize = 1 }: ItemListStylesProps): string => `${100 / pageSize}%`,
  color: ({ disabled }: ItemListStylesProps) =>
    disabled ? themeColor.disabledTextGrey : themeColor.defaultTextColor,
  letterSpacing: '0.01vw',
  lineHeight: 1.2,
  borderBottom: `1px solid ${themeColor.secondaryLight}`,
  width: '100%',
  fontSize: vh(2.08),
  boxSizing: 'border-box',
};

const baseInlineIcon: CSSProperties = {
  height: '3.125vh',
  marginRight: '0.732vw',
  verticalAlign: 'middle',
};

const baseItemFont: CSSProperties = {
  fontSize: rem(14 / 16),
  '@media (min-aspect-ratio: 16/9)': {
    fontSize: rem(18 / 16),
  },
  '@media (max-height: 720px)': {
    fontSize: rem(0.8),
    lineHeight: rem(0.95),
    maxHeight: rem(1.9),
  },
  '@media (max-height: 600px)': {
    fontSize: rem(0.75),
    lineHeight: rem(0.9),
    maxHeight: rem(1.8),
  },
};

const baseListContainer: CreateCSSProperties = {
  height: ({ ejListSize = 1, pageSize = 1 }: ItemListStylesProps): string =>
    `${(100 / ejListSize) * pageSize}%`,
  zIndex: 1,
  borderBottom: `1px solid ${themeColor.secondaryLight}`,
  boxSizing: 'border-box',
};

export const styles = makeStyles(theme => ({
  upperCase: {
    textTransform: 'uppercase',
  },
  container: {
    position: 'relative',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    border: `1px solid ${themeColor.secondaryLight}`,
    borderTop: 'none',
    overflowY: 'auto',
    background: themeColor.lightBackground,
    '& .text': {
      color: themeColor.defaultTextColor,
      fontWeight: 500,
      fontSize: rem(1.5),
      margin: '10px 0',
      // 16:9
      '@media (height: 576px) and (width: 1024px)': {
        fontSize: rem(1.2),
      },
      '@media (height: 900px) and (width: 1600px)': {
        fontSize: rem(1.7),
      },
      '@media (height: 1080px) and (width: 1920px)': {
        fontSize: rem(2),
      },

      // 4:3
      '@media (aspect-ratio: 4/3)': {
        fontSize: rem(1.2),
      },
      '@media (height: 1200px) and (width: 1600px)': {
        fontSize: rem(1.7),
      },
    },
    borderBottom: 'none',
  },
  containerItem: {
    alignItems: 'flex-end',
  },
  returnItem: {
    verticalAlign: 'middle',
    height: '3vh',
    width: '2vw',
    marginRight: '0.732vw',
  },
  emptyCartContainer: {
    textAlign: 'center',
    justifyContent: 'center',
  },
  emptyCartBarcode: {
    marginTop: '1.30208vh;',
  },
  textCenter: {
    textAlign: 'center',
  },
  textRight: {
    textAlign: 'right',
    fontSize: vh(2.08),
    '@media (aspect-ratio: 4/3)': {
      fontSize: vh(1.61),
    },
    letterSpacing: '0.03vw',
    fontWeight: 'bold',
  },
  line: {
    ...lineBase,
  },
  lineItem: {
    ...lineBase,
    '@media (aspect-ratio: 4/3)': {
      fontSize: vh(1.61),
    },
    fontWeight: 'bold',
  },
  noBottomBorder: {
    borderBottom: 'none',
  },
  selected: {
    background: themeColor.selectedPrimary,
  },
  customerDisplaySelected: {
    background: baseColor.verylightgrey,
  },
  lineItemContainer: {
    padding: '0 0.732vw',
    position: 'relative',
    '@media (max-height: 720px)': {
      padding: '0 0.44vw',
    },
    '@media (max-height: 600px)': {
      padding: '0 0.3vw',
    },
  },
  lineItemContainerCustomer: {
    paddingRight: '2.9vw',
  },
  productIconContainer: {
    height: '100%',
    '& span': {
      height: '100%',
      padding: 0,
      margin: 0,
    },
  },
  lineItemTitleContainer: {
    height: '100%',
    flexWrap: 'nowrap',
    alignItems: 'center',
  },
  lineItemTitle: {
    wordBreak: 'break-word',
    fontWeight: 500,
    maxHeight: rem(3.3),
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
    ...baseItemFont,
  },
  lineItemFont: {
    ...baseItemFont,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  inlineIcon: {
    width: '1.75vw',
    ...baseInlineIcon,
  },
  inlineIconSelected: {
    width: '1.75vw',
    ...baseInlineIcon,
  },
  icon: {
    width: '2.85vw',
    height: '4.16vh',
  },
  voided: {
    width: 'calc(100% - 10px)',
    // needs to be px to maintain consistency
    height: '2px',
    top: '50%',
    background: themeColor.alert,
    position: 'absolute',
    left: '1.21%',
  },
  arrowButtons: {
    color: theme.palette.info.main,
  },
  navigationContainer: {
    textAlign: 'center',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
  },
  maincontainerItem: {
    flexWrap: 'nowrap',
    flexGrow: 1,
  },
  maincontainerNavigationItem: {
    flexDirection: 'row',

    '& button': {
      minHeight: '6vh',
      height: '6.25vh',
    },
  },
  saleModeNavigationContainer: {
    flexDirection: 'row',
  },
  emptyFlexContainer: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  notEmptyAlignContainer: {
    alignItems: 'center',
  },
  itemListContainerMaxHeight: {
    height: '54vh',
  },
  itemListContainerMaxHeightCustomerDisplay: {
    height: '59vh',
  },
  clickable: {
    cursor: 'pointer',
  },
  transactionVoidedContainer: {
    justifyContent: 'center',
  },
  transactionVoidedText: {
    fontSize: vh(2.305),
    fontWeight: 500,
    textAlign: 'center',
    marginBottom: '1.921vh',
  },
  transactionVoidedImageContainer: {
    justifyContent: 'center',
  },
  returnModeWatermark: {
    padding: '0.38vh 0.732vw',
    borderBottom: `1px solid ${themeColor.secondaryLight}`,
    opacity: 0.5,
    '@media (aspect-ratio: 4/3)': {
      fontSize: rem(1.6),
    },
    /* 16/9 */
    '@media (height: 576px) and (width: 1024px)': {
      fontSize: rem(1.5),
    },
    '@media (height: 900px) and (width: 1600px)': {
      fontSize: rem(2.2),
    },
    '@media (height: 1080px) and (width: 1920px)': {
      fontSize: rem(2.7),
    },
    /* 4/3 */
    '@media (height: 1200px) and (width: 1600px)': {
      fontSize: rem(2.2),
    },
    fontFamily: 'Raleway',
    fontStyle: 'normal',
    fontWeight: 'bold',
    fontSize: rem(2),
    whiteSpace: 'nowrap',
    color: themeColor.returnModeWatermark,
    mixBlendMode: 'normal',
  },
  returnModeWatermarkCustomer: {
    padding: '1vh',
    borderBottom: `1px solid ${themeColor.secondaryLight}`,
    opacity: 0.5,
    fontFamily: 'Raleway',
    fontStyle: 'normal',
    fontWeight: 'bold',
    fontSize: rem(2),
    whiteSpace: 'nowrap',
    color: themeColor.returnModeWatermark,
    mixBlendMode: 'normal',
  },
  itemListContainer: {
    ...baseListContainer,
  },

  bottomItemsPinned: {
    borderBottom: 'none !important',
  },

  pinnedItemListContainer: {
    '& > *:first-child': {
      borderTop: `1px solid ${baseColor.gullGray}`,
    },
    borderBottom: 'none !important',
  },

  tenderEligibilityListContainer: {
    '& > div': {
      borderBottom: 'none !important',
      color: baseColor.darkBlue,
    },
    '& > *:first-child': {
      borderTop: `1px solid ${baseColor.gullGray}`,
    },
    '& > *:last-child': {
      borderBottom: `1px solid ${baseColor.gullGray} !important`,
    },
    borderBottom: 'none !important',
  },

  itemListContainerCustomer: {
    ...baseListContainer,
    overflow: 'hidden !important',
  },
  taxExemptionContainer: {
    marginTop: '2vh',
  },
  iconContainer: {
    marginRight: '0.7vw',
    display: 'flex',
    alignItems: 'center',
    '& img, svg': {
      width: 'auto',
      height: '45% !important',
      maxWidth: '5vw',
      padding: '0',
      margin: '0',
      alignSelf: 'center',
    },
  },
  customContainerWidth: {
    width: '50%',
  },
  checkboxTBR: {
    paddingLeft: 'inherit',
    paddingTop: 'inherit',
    paddingBottom: 'inherit',
  },
}));
